import { useCountByStatus } from "@/api/auction/auction";
import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { FieldConfig } from "@/config/forms/general";
import { Auction, Multipleprint, MultipleprintInAuction } from "@/lib/types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronLeft, PlusIcon, Trash2Icon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import MultipleprintInAuctionDrawer from "./drawer/MultipleprintInAuctionDrawer";

interface ComplexLinkProps {
  id: string;
  multipleprint: Multipleprint;
  fields: FieldConfig[] | undefined;
  type: LinkableType;
}

/**
 * MultipleprintInAuctionLinks component
 * @returns {JSX.Element}
 */
const MultipleprintInAuctionLinks = ({
  id,
  multipleprint,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "multipleprintInAuction",
    type,
  );

  const { mutate: deleteMultipleprintInAuction } = useDeleteComplexLink(
    id,
    "multipleprintInAuction",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = async (id: string) => {
    await deleteMultipleprintInAuction({
      id,
      relation: "multipleprintInAuction",
    });
  };

  const [searchContent, setSearchContent] = useState("");

  const columns: ColumnDef<MultipleprintInAuction, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "auction.ventesDescriptionNumeroVente",
        header: t("ventesDescriptionNumeroVente"),
        cell: ({ row }: { row: Row<MultipleprintInAuction> }) => {
          return (
            <TextCell
              value={row.original.auction.ventesDescriptionNumeroVente}
            />
          );
        },
      },
      {
        accessorKey: "auction.ventesDescriptionCommissaires",
        header: t("ventesDescriptionCommissaires"),
        cell: ({ row }: { row: Row<MultipleprintInAuction> }) => {
          return (
            <TextCell
              value={row.original.auction.ventesDescriptionCommissaires}
            />
          );
        },
      },
      {
        accessorKey: "auction.ventesDescriptionDate",
        header: t("ventesDescriptionDate"),
        cell: ({ row }: { row: Row<MultipleprintInAuction> }) => {
          return (
            <TextCell value={row.original.auction.ventesDescriptionDate} />
          );
        },
      },
      {
        accessorKey: "auction.ventesDescriptionVille",
        header: t("ventesDescriptionVille"),
        cell: ({ row }: { row: Row<MultipleprintInAuction> }) => {
          return (
            <TextCell value={row.original.auction.ventesDescriptionVille} />
          );
        },
      },
      {
        accessorKey: "auction.ventesDescriptionPays",
        header: t("ventesDescriptionPays"),
        cell: ({ row }: { row: Row<MultipleprintInAuction> }) => {
          return (
            <TextCell value={row.original.auction.ventesDescriptionPays} />
          );
        },
      },
      {
        accessorKey: "ventesDansEstampesMultiplesDescriptionNumeroLot",
        header: t("ventesDansEstampesMultiplesDescriptionNumeroLot"),
        cell: ({ row }: { row: Row<MultipleprintInAuction> }) => {
          return (
            <TextCell
              value={
                row.original.ventesDansEstampesMultiplesDescriptionNumeroLot
              }
            />
          );
        },
      },
      {
        accessorKey: "ventesDansEstampesMultiplesDescriptionExemplaireVendu",
        header: t("ventesDansEstampesMultiplesDescriptionExemplaireVendu"),
        cell: ({ row }: { row: Row<MultipleprintInAuction> }) => {
          return (
            <TextCell
              value={
                row.original
                  .ventesDansEstampesMultiplesDescriptionExemplaireVendu
              }
            />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<MultipleprintInAuction> }) => {
          const data = row.original as MultipleprintInAuction;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(data.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <MultipleprintInAuctionDrawer
                key={row.original.id}
                fields={fields}
                data={data}
                queryId={id}
                type={type}
              />
              {type === "auction" && (
                <ShowElements
                  type="multipleprint"
                  id={row.original.multipleprint.id}
                  elements={[
                    {
                      label: t(
                        "estampesMultiplesDescriptionNumeroEnregistrement",
                      ),
                      value:
                        row.original.multipleprint
                          .estampesMultiplesDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t("estampesMultiplesDescriptionResume"),
                      value:
                        row.original.multipleprint
                          .estampesMultiplesDescriptionResume,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.multipleprint.attachments,
                      type: "image",
                    },
                  ]}
                />
              )}
              {type === "multipleprint" && (
                <ShowElements
                  type="auction"
                  id={row.original.auction.id}
                  elements={[
                    {
                      label: t("ventesDescriptionNumeroVente"),
                      value: row.original.auction.ventesDescriptionNumeroVente,
                      type: "text",
                    },
                    {
                      label: t("ventesDescriptionResume"),
                      value: row.original.auction.ventesDescriptionResume,
                      type: "richtext",
                    },
                  ]}
                />
              )}
            </div>
          );
        },
      },
    ];
  }, [isAddMode]);

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">{t("linkedAuctions")}</h2>
        {!isAddMode && (
          <Button variant="outline" onClick={handleAddMode}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
            {/*<Button variant="default" onClick={handleAddMode}>
              <PlusIcon className="w-4 h-4 mr-2" />
              {t("newAuction")}
            </Button>*/}
          </div>
        )}
      </div>
      <MultipleprintToAuctionLinks
        id={id}
        relatedData={relatedData}
        fields={fields}
        multipleprint={multipleprint}
        isAddMode={isAddMode}
        columnsLink={columns}
        searchContent={searchContent}
      />
    </div>
  );
};

type MultipleprintInAuctionLinksProps =
  LinksTableProps<MultipleprintInAuction> & {
    multipleprint: Multipleprint | undefined;
  };

//Composant pour Oeuvres
const MultipleprintToAuctionLinks = ({
  id,
  relatedData,
  fields,
  multipleprint,
  isAddMode,
  columnsLink,
  searchContent,
}: MultipleprintInAuctionLinksProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: countByStatus } = useCountByStatus();
  const totalSize = countByStatus?.published + countByStatus?.draft;
  const { data: notAlreadyRelatedAuctions } = useGetNotRelatedResource<Auction>(
    {
      resultType: "auction",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const columnsAuctions: ColumnDef<Auction, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "ventesDescriptionNumeroVente",
        header: t("ventesDescriptionNumeroVente"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionNumeroVente} />;
        },
      },
      {
        accessorKey: "ventesDescriptionTitre",
        header: t("ventesDescriptionTitre"),
        size: 500,
        cell: (props) => {
          return <HtmlCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "ventesDescriptionDate",
        header: t("ventesDescriptionDate"),
        size: 100,
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionDate} />;
        },
      },
      {
        accessorKey: "ventesDescriptionVille",
        header: t("ventesDescriptionVille"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionVille} />;
        },
      },
      {
        accessorKey: "ventesDescriptionPays",
        header: t("ventesDescriptionPays"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionPays} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as Auction;
          if (!multipleprint) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="auction"
                id={row.original.id}
                elements={[
                  {
                    label: t("ventesDescriptionNumeroVente"),
                    value: row.original.ventesDescriptionNumeroVente,
                    type: "text",
                  },
                  {
                    label: t("ventesDescriptionResume"),
                    value: row.original.ventesDescriptionResume,
                    type: "richtext",
                  },
                ]}
              />
              <MultipleprintInAuctionDrawer
                key={row.original.id}
                fields={fields}
                queryId={id}
                type={"multipleprint"}
                data={{
                  id: "",
                  auction: data,
                  multipleprint: multipleprint,
                  ventesDansEstampesMultiplesDescriptionNumeroLot: "",
                  ventesDansEstampesMultiplesDescriptionReproduitPage: "",
                  ventesDansEstampesMultiplesDescriptionEstimationHaute: "",
                  ventesDansEstampesMultiplesDescriptionEstimationBasse: "",
                  ventesDansEstampesMultiplesDescriptionVendu: false,
                  ventesDansEstampesMultiplesDescriptionResultatNonListe: false,
                  ventesDansEstampesMultiplesDescriptionNonVendu: false,
                  ventesDansEstampesMultiplesDescriptionNonVenu: false,
                  ventesDansEstampesMultiplesDescriptionRetire: false,
                  ventesDansEstampesMultiplesDescriptionMiseAPrix: "",
                  ventesDansEstampesMultiplesDescriptionPrixMarteau: "",
                  ventesDansEstampesMultiplesDescriptionPrixAvecFrais: "",
                  ventesDansEstampesMultiplesDescriptionExemplaireVendu: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [fields, id, multipleprint, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsAuctions}
          data={notAlreadyRelatedAuctions?.data ?? []}
          pagination={
            isAddMode
              ? {
                  pagination: getPagination,
                  setPage: setPagination,
                  totalSize: notAlreadyRelatedAuctions?.totalSize ?? 0,
                }
              : undefined
          }
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          pagination={
            isAddMode
              ? {
                  pagination: getPagination,
                  setPage: setPagination,
                  totalSize: totalSize,
                }
              : undefined
          }
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      isAddMode,
      columnsAuctions,
      notAlreadyRelatedAuctions?.data,
      notAlreadyRelatedAuctions?.totalSize,
      getPagination,
      columnsLink,
      relatedData,
      totalSize,
    ],
  );

  return datatableConf;
};

export default MultipleprintInAuctionLinks;
